import React, { useState } from 'react';
import { Card, Button, Alert, ProgressBar, Modal, Table, Spinner } from 'react-bootstrap';
import { FaFileUpload, FaCheckCircle, FaExclamationTriangle, FaEye, FaCloudUploadAlt, FaDownload, FaFileAlt } from 'react-icons/fa';
import Api from "../../services/Api";
import ApiMServer from '../../services/ApiMServer';

/**
 * CSV Upload component for file uploads with validation
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onFileUpload - Function to handle file upload
 * @param {File} props.file - The currently selected file
 * @param {string} props.error - Error message if validation fails
 * @param {string} props.title - Card title
 * @param {string} props.description - Description text
 * @param {Array} props.requiredColumns - Array of required column names to check
 * @param {number} props.maxFileSize - Maximum file size in MB
 * @param {Function} props.onProcessSuccess - Função chamada quando o processamento for bem-sucedido, recebe a lista JSON
 * @param {Array} props.companies - Lista de empresas com equipamentos
 */
const CsvUploadCard = ({
  onFileUpload,
  file,
  error,
  title = "Importar Dados CSV",
  description = "Carregue um arquivo CSV para importar dados ao sistema.",
  requiredColumns = [],
  maxFileSize = 5,
  onProcessSuccess,
  companies = []
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [previewData, setPreviewData] = useState({ headers: [], rows: [] });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState({ success: false, message: '' });

  const handleFileSelect = (event) => {
    if (onFileUpload && event.target.files.length > 0) {
      onFileUpload(event);
      // Reinicia o resultado do processamento quando um novo arquivo é selecionado
      setProcessResult({ success: false, message: '' });
    }
  };

  // Format required columns for display
  const formattedColumns = requiredColumns.length
    ? `contendo as colunas "${requiredColumns.join('", "')}"`
    : '';

  // Extrai todos os equipamentos das empresas
  const getAllEquipments = () => {
    // Verifica se companies está definido e é um array
    if (!companies || !Array.isArray(companies)) {
      return [];
    }

    // Utiliza flatMap para concatenar todos os arrays de equipamentos em um único array
    const allEquipments = companies.flatMap(company => {
      // Verifica se a empresa possui a propriedade equipments e se é um array
      if (company && company.equipments && Array.isArray(company.equipments)) {
        // Mapeia cada equipamento para incluir o ID da empresa
        return company.equipments.map(equipment => ({
          serial: equipment,
          companyId: company._id,
        }));
      }
      return []; // Retorna array vazio se a empresa não tiver equipamentos
    });

    return allEquipments;
  };

  // Função para visualizar o conteúdo do arquivo CSV
  const handlePreviewFile = () => {
    if (!file) return;

    setPreviewLoading(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      const csvContent = event.target.result;
      const rows = csvContent.split('\n');

      if (rows.length > 0) {
        // Identifica o delimitador (vírgula ou ponto-e-vírgula)
        const firstRow = rows[0];
        const delimiter = firstRow.includes(';') ? ';' : ',';

        // Processa cabeçalhos e linhas
        const headers = rows[0].split(delimiter).map(header => header.trim());

        const dataRows = [];
        // Limita a visualização a 100 linhas para arquivos grandes
        const maxRows = Math.min(rows.length, 100);

        for (let i = 1; i < maxRows; i++) {
          if (rows[i].trim() !== '') {
            const cells = rows[i].split(delimiter).map(cell => cell.trim());
            dataRows.push(cells);
          }
        }

        setPreviewData({
          headers,
          rows: dataRows,
          totalRows: rows.length - 1,
          shownRows: dataRows.length
        });
      }

      setPreviewLoading(false);
      setShowPreview(true);
    };

    reader.onerror = () => {
      setPreviewLoading(false);
      // Poderia adicionar um tratamento de erro aqui
    };

    reader.readAsText(file);
  };

  // Função para processar o arquivo e transformar em JSON
  const handleProcessFile = () => {
    if (!file) return;

    setProcessing(true);
    setProcessResult({ success: false, message: '' });

    try {
      const reader = new FileReader();

      reader.onload = async (event) => {
        const csvContent = event.target.result;
        const rows = csvContent.split('\n');

        if (rows.length > 0) {
          // Identifica o delimitador (vírgula ou ponto-e-vírgula)
          const firstRow = rows[0];
          const delimiter = firstRow.includes(';') ? ';' : ',';

          // Processa cabeçalhos
          const headers = rows[0].split(delimiter).map(header => header.trim().toLowerCase());

          // Encontra os índices das colunas de serial e id
          const serialIndex = headers.findIndex(header =>
            header === 'serial' || header === 'código' || header === 'codigo' ||
            header === 'id' || header === 'número' || header === 'numero');

          const nameIndex = headers.findIndex(header => header === 'id');

          if (serialIndex === -1 || nameIndex === -1) {
            throw new Error('Não foi possível identificar as colunas de serial e id no arquivo CSV');
          }

          // Transforma as linhas em objetos JSON
          const jsonData = [];

          for (let i = 1; i < rows.length; i++) {
            if (rows[i].trim() !== '') {
              const cells = rows[i].split(delimiter).map(cell => cell.trim());

              if (cells.length >= Math.max(serialIndex, nameIndex) + 1) {
                jsonData.push({
                  serial: cells[serialIndex],
                  id: cells[nameIndex]
                });
              }
            }
          }

          // Obtém todos os equipamentos das empresas
          const allEquipments = getAllEquipments();
          // Extrai apenas os seriais para facilitar a filtragem
          const allSerials = allEquipments.map(item => item.serial);

          // Filtra os dados do CSV para incluir apenas os seriais que existem em allEquipments
          const processedData = jsonData
            .filter(item => allSerials.includes(item.serial))
            .map(item => {
              // Encontra o equipamento correspondente
              const matchedEquipment = allEquipments.find(
                eq => eq.serial === item.serial
              );

              // Retorna os dados combinados
              return {
                serialnumber: item.serial,
                nameEquipament: item.id,
                companyId: matchedEquipment.companyId
              };
            });

          try {
            const { data } = await ApiMServer.post('/company/addlocalequip/list', processedData);
            // Atualiza o estado com o resultado do processamento
            setProcessResult({
              success: true,
              message: `Processado com sucesso! ${data.updated.length} equipamentos válidos de ${jsonData.length} registros no CSV.`,
              data: processedData
            });

          } catch (error) {
            console.log(error);
            // Extrai mensagem de erro da resposta da API ou usa uma mensagem padrão
            let errorMessage = 'Ocorreu um erro ao processar os equipamentos.';
            
            if (error.response && error.response.data) {
              // Se a API retornou uma mensagem de erro estruturada
              if (error.response.data.message) {
                errorMessage = error.response.data.message;
              } else if (typeof error.response.data === 'string') {
                errorMessage = error.response.data;
              }
            } else if (error.message) {
              // Se é um erro JavaScript padrão
              errorMessage = error.message;
            }
            
            // Atualiza o estado com a mensagem de erro
            setProcessResult({
              success: false,
              message: `Não foi possível processar os equipamentos: ${errorMessage}`,
              error: error
            });
            
            // Exibe a notificação visual do erro
            setProcessing(false);
          }

        } else {
          throw new Error('O arquivo CSV está vazio');
        }

        setProcessing(false);
      };

      reader.onerror = () => {
        setProcessing(false);
        setProcessResult({
          success: false,
          message: 'Erro ao ler o arquivo CSV'
        });
      };

      reader.readAsText(file);

    } catch (error) {
      console.error('Erro ao processar o arquivo:', error);

      // Atualiza o estado com a mensagem de erro
      setProcessResult({
        success: false,
        message: error.message || 'Ocorreu um erro ao processar o arquivo. Tente novamente.'
      });

      setProcessing(false);
    }
  };

  // Função para gerar um CSV modelo
  const generateCsvTemplate = () => {
    // Cabeçalhos do modelo (pode ser adaptado conforme necessário)
    const headers = ["serial", "id"];
    
    // Exemplo de dados para o modelo
    const exampleData = [
      ["SIMT-000001", "Equipamento 1"],
      ["BIMT-000002", "Equipamento 2"]
    ];
    
    // Cria o conteúdo do CSV
    let csvContent = headers.join(",") + "\n";
    exampleData.forEach(row => {
      csvContent += row.join(",") + "\n";
    });
    
    return csvContent;
  };
  
  // Função para baixar o modelo de CSV
  const downloadCsvTemplate = () => {
    const csvContent = generateCsvTemplate();
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    
    // Cria um link para download e simula o clique
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "modelo_csv_equipamentos.csv");
    document.body.appendChild(link);
    link.click();
    
    // Limpa o objeto URL após o download
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };
  
  // Função para exibir o modelo em um modal
  const handleViewTemplate = () => {
    const csvContent = generateCsvTemplate();
    const rows = csvContent.split('\n');
    
    if (rows.length > 0) {
      const headers = rows[0].split(',').map(header => header.trim());
      
      const dataRows = [];
      for (let i = 1; i < rows.length; i++) {
        if (rows[i].trim() !== '') {
          const cells = rows[i].split(',').map(cell => cell.trim());
          dataRows.push(cells);
        }
      }
      
      setPreviewData({
        headers,
        rows: dataRows,
        totalRows: dataRows.length,
        shownRows: dataRows.length
      });
      
      setShowTemplateModal(true);
    }
  };

  return (
    <>
      <Card
        className="shadow-sm mb-2"
        style={{
          borderRadius: '10px',
          border: 'none',
          overflow: 'hidden'
        }}
      >
        <Card.Header
          className="d-flex align-items-center justify-content-between"
          style={{
            background: 'linear-gradient(135deg, rgba(143, 173, 21, 0.2), rgba(28, 66, 32, 0.1))',
            border: 'none'
          }}
        >
          <div className="d-flex align-items-center">
            <FaFileUpload className="me-2" size={18} style={{ color: '#8fad15' }} />
            <h5 className="mb-0 fw-bold" style={{ color: '#1c4220' }}>{title}</h5>
          </div>
          <div>
            <Button
              variant="link"
              className="text-decoration-none p-0 d-flex align-items-center"
              onClick={() => setShowTemplateModal(true)}
              style={{ color: '#1c4220' }}
            >
              <FaFileAlt className="me-1" size={14} />
              <small>Ver modelo</small>
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-4">
          <p className="text-muted mb-3">
            {description} {formattedColumns}
          </p>

          <div className="d-flex flex-column flex-md-row align-items-center gap-3">
            <input
              type="file"
              id="csvFileInput"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <Button
              variant="primary"
              onClick={() => document.getElementById('csvFileInput').click()}
              className="px-4 py-2 d-flex align-items-center justify-content-center"
              style={{
                background: 'linear-gradient(135deg, rgba(143, 173, 21, 0.9), rgba(28, 66, 32, 0.9))',
                border: 'none',
                borderRadius: '6px',
                transition: 'all 0.3s ease',
                minWidth: '200px',
                boxShadow: '0 4px 15px rgba(28, 66, 32, 0.2)'
              }}
            >
              <FaFileUpload className="me-2" />
              Selecionar arquivo CSV
            </Button>

            {file && (
              <span className="text-muted d-flex align-items-center">
                <FaCheckCircle className="me-2" style={{ color: '#8fad15' }} />
                {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </span>
            )}
          </div>

          {error && (
            <Alert
              variant="danger"
              className="mt-3 d-flex align-items-center"
              style={{ borderRadius: '6px' }}
            >
              <FaExclamationTriangle className="me-2" />
              {error}
            </Alert>
          )}

          {processResult.message && (
            <Alert
              variant={processResult.success ? "success" : "danger"}
              className="mt-3 d-flex align-items-center"
              style={{ borderRadius: '6px' }}
            >
              {processResult.success ?
                <FaCheckCircle className="me-2" style={{ color: '#8fad15' }} /> :
                <FaExclamationTriangle className="me-2" />
              }
              {processResult.message}
            </Alert>
          )}

          {file && !error && (
            <Alert
              variant="success"
              className="mt-3"
              style={{
                borderRadius: '6px',
                background: 'rgba(143, 173, 21, 0.15)',
                color: '#1c4220',
                border: '1px solid rgba(143, 173, 21, 0.3)'
              }}
            >
              <div className="d-flex align-items-center mb-2">
                <FaCheckCircle className="me-2" style={{ color: '#8fad15' }} />
                <span className="fw-bold">Arquivo lido com sucesso!</span>
              </div>
              <p className="mb-2">O arquivo "{file.name}" está pronto para ser processado.</p>

              <div className="d-flex justify-content-between align-items-center">
                <ProgressBar
                  variant="success"
                  now={100}
                  style={{
                    height: '6px',
                    borderRadius: '3px',
                    backgroundColor: 'rgba(28, 66, 32, 0.1)',
                    flex: '1 1 auto'
                  }}
                />

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-success"
                    className="ms-3 d-flex align-items-center"
                    onClick={handlePreviewFile}
                    disabled={previewLoading}
                    style={{
                      borderColor: 'rgba(143, 173, 21, 0.5)',
                      color: '#1c4220',
                    }}
                  >
                    <FaEye className="me-2" />
                    {previewLoading ? 'Carregando...' : 'Visualizar'}
                  </Button>

                  <Button
                    variant="primary"
                    className="ms-2 d-flex align-items-center"
                    onClick={handleProcessFile}
                    disabled={processing}
                    style={{
                      background: 'linear-gradient(135deg, rgba(28, 66, 32, 0.9), rgba(28, 66, 32, 0.8))',
                      border: 'none',
                      borderRadius: '6px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 10px rgba(28, 66, 32, 0.15)'
                    }}
                  >
                    {processing ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Processando...
                      </>
                    ) : (
                      <>
                        <FaCloudUploadAlt className="me-2" />
                        Processar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Alert>
          )}

          <div className="mt-3 d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Formatos aceitos: .csv | Tamanho máximo: {maxFileSize}MB
            </small>
            <Button
              variant="link"
              className="p-0 text-decoration-none"
              onClick={downloadCsvTemplate}
              style={{ color: '#1c4220' }}
            >
              <FaDownload className="me-1" size={12} />
              <small>Baixar modelo CSV</small>
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Modal para visualização dos dados do CSV */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, rgba(143, 173, 21, 0.1), rgba(28, 66, 32, 0.05))',
            borderBottom: '1px solid rgba(143, 173, 21, 0.2)'
          }}
        >
          <Modal.Title style={{ color: '#1c4220' }}>
            <FaEye className="me-2" />
            Visualização de {file?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {previewData.rows.length > 0 ? (
            <>
              <p className="text-muted mb-3">
                Mostrando {previewData.shownRows} de {previewData.totalRows} linhas
              </p>
              <div className="table-responsive">
                <Table striped bordered hover size="sm">
                  <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                    <tr>
                      {previewData.headers.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center p-4">
              <FaExclamationTriangle className="mb-3" size={30} style={{ color: '#8fad15' }} />
              <p>Não foi possível extrair dados deste arquivo.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid rgba(143, 173, 21, 0.2)' }}>
          <Button
            variant="secondary"
            onClick={() => setShowPreview(false)}
            style={{
              background: 'rgba(28, 66, 32, 0.7)',
              borderColor: 'rgba(28, 66, 32, 0.9)',
            }}
          >
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para visualização do modelo CSV */}
      <Modal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, rgba(143, 173, 21, 0.1), rgba(28, 66, 32, 0.05))',
            borderBottom: '1px solid rgba(143, 173, 21, 0.2)'
          }}
        >
          <Modal.Title style={{ color: '#1c4220' }}>
            <FaFileAlt className="me-2" />
            Modelo de Arquivo CSV
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Este é o formato esperado para o arquivo CSV. Certifique-se de que seu arquivo siga este modelo:
          </p>
          <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                <tr>
                  {previewData.headers.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="mt-3">
            <Alert variant="info" className="mb-0">
              <strong>Importante:</strong>
              <ul className="mb-0 ps-3 mt-1">
                <li>A coluna <strong>serial</strong> deve conter o número de série do equipamento</li>
                <li>A coluna <strong>id</strong> deve conter o identificador do equipamento</li>
                <li>O arquivo não deve conter cabeçalhos ou rodapés adicionais</li>
                <li>Certifique-se de que todos os seriais existam no sistema</li>
              </ul>
            </Alert>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid rgba(143, 173, 21, 0.2)' }}>
          <Button
            variant="success"
            onClick={downloadCsvTemplate}
            className="me-2"
            style={{
              background: 'linear-gradient(135deg, rgba(143, 173, 21, 0.9), rgba(28, 66, 32, 0.9))',
              border: 'none',
            }}
          >
            <FaDownload className="me-2" />
            Baixar Modelo
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowTemplateModal(false)}
            style={{
              background: 'rgba(28, 66, 32, 0.7)',
              borderColor: 'rgba(28, 66, 32, 0.9)',
            }}
          >
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CsvUploadCard;